/**
 * src/components/feature/viewer/ViewerHeader.js
 * 
 * Standardized Viewer Header
 * - Single source of truth for all viewer types.
 * - Robust scroll handling (Throttling, Safe Areas).
 * - Conditional rendering based on viewerType.
 */
export class ViewerHeader {
    /**
     * @param {HTMLElement} container - Container to mount into (usually document.body)
     * @param {Object} props
     * @param {string} props.title - Series Title
     * @param {string} props.viewerType - 'webtoon' | 'novel' | 'davinci'
     * @param {Function} props.onBack - Back button handler
     * @param {Function} props.onSettings - Settings button handler (Novel/DaVinci)
     * @param {Function} props.onSearch - Search button handler
     * @param {string} [props.backIcon='arrow_back'] - Custom back icon name (e.g. 'close')
     * @param {string} [props.backIcon='arrow_back'] - Custom back icon name (e.g. 'close')
     */
    constructor(container, { title, viewerType = 'webtoon', onBack, onSettings, onSearch, backIcon = 'arrow_back' }) {
        this.container = container;
        this.title = title;
        this.viewerType = viewerType;
        this.onBack = onBack;
        this.onSettings = onSettings;
        this.onSearch = onSearch;
        this.backIcon = backIcon;

        this.element = null;
        this.isVisible = true;
        this._lockedVisible = false;

        // Scroll Logic State
        this._scrollTarget = window;
        this._scrollHandler = null;
        this._rafId = null;
        this._lastScrollY = 0;
        this._lastScrollX = 0; // For horizontal webtoon
        this.SAFE_AREA_TOP = 60; // Always show if within 60px of top
        this.DELTA_THRESHOLD = 10; // Ignore micro-scrolls

        // Cleanup previous instance if exists (Global Singleton Pattern within Session)
        if (window.activeViewerHeader) {
            try { window.activeViewerHeader.destroy(); } catch (e) { }
        }
        window.activeViewerHeader = this;

        this.init();
    }

    init() {
        this.render();
        this.bindEvents();
        this.setupScrollListener();
    }

    render() {
        // Remove existing header if any
        const existing = document.getElementById('viewer-header-component');
        if (existing) existing.remove();

        const header = document.createElement('header');
        header.id = 'viewer-header-component';
        // [FIX] z-index Standardized: 100 (Footer/Header) < 500 (FAB) < 2000 (Modal)
        header.className = "fixed top-0 left-0 w-full bg-zinc-900/95 backdrop-blur-md shadow-lg border-b border-white/5 z-[100] transition-transform duration-300 transform translate-y-0";

        // Settings Button Logic
        // Webtoon: Hidden
        // Novel/DaVinci: Visible
        const showSettings = this.viewerType === 'novel' || this.viewerType === 'davinci';

        header.innerHTML = `
            <div class="w-full h-14 max-w-5xl mx-auto px-4 flex items-center justify-between">
                
                <!-- Left: Back & Title -->
                <div class="flex items-center gap-3 overflow-hidden">
                    <button id="vh-btn-back" class="p-2 -ml-2 rounded-full hover:bg-white/10 text-zinc-300 hover:text-white transition-colors flex-shrink-0">
                        <span class="material-symbols-outlined text-2xl">${this.backIcon || 'arrow_back'}</span>
                    </button>
                    <h1 class="text-gray-200 font-bold text-sm truncate select-none">${this.title}</h1>
                </div>

                <!-- Right: Actions -->
                <div class="flex items-center gap-1.5 flex-shrink-0">
                    <button id="vh-btn-search" class="p-2 rounded-full hover:bg-white/10 text-zinc-300 hover:text-white transition-colors">
                        <span class="material-symbols-outlined text-xl">search</span>
                    </button>
                    
                    ${showSettings ? `
                    <button id="vh-btn-settings" class="p-2 -mr-2 rounded-full hover:bg-white/10 text-zinc-300 hover:text-white transition-colors">
                        <span class="material-symbols-outlined text-xl">settings</span>
                    </button>
                    ` : ''}
                </div>
            </div>
        `;

        // Mount to container (Supports Preview Modal)
        // [FIX] Use this.container instead of document.body to ensure correct z-index stacking in modals
        this.container.appendChild(header);
        this.element = header;
    }

    bindEvents() {
        const btnBack = this.element.querySelector('#vh-btn-back');
        const btnSearch = this.element.querySelector('#vh-btn-search');
        const btnSettings = this.element.querySelector('#vh-btn-settings');

        if (btnBack) btnBack.onclick = (e) => {
            e.stopPropagation();
            if (this.onBack) this.onBack();
        };

        if (btnSearch) btnSearch.onclick = (e) => {
            e.stopPropagation();
            if (this.onSearch) this.onSearch();
        };

        if (btnSettings) btnSettings.onclick = (e) => {
            e.stopPropagation();
            if (this.onSettings) this.onSettings();
        };
    }

    // --- Robust Scroll Logic ---

    setScrollTarget(target) {
        // 1. Remove old listener
        if (this._scrollHandler && this._scrollTarget) {
            this._scrollTarget.removeEventListener('scroll', this._scrollHandler);
        }

        // 2. Update target
        this._scrollTarget = target || window;

        // 3. Re-attach
        this.setupScrollListener();
    }

    setupScrollListener() {
        // [FIX] Comic Mode (Scroll Snap) Incompatibility
        // If "comic" mode, we DISABLE scroll-based hiding to prevent flickering.
        // The header is toggled via TAP only in this mode.
        if (this.viewerType && this.viewerType.startsWith('comic')) {
            console.log("[ViewerHeader] Scroll listener disabled for Comic Mode (Snap Risk).");
            return;
        }

        this._scrollHandler = () => {
            if (this._rafId) return; // Throttling
            this._rafId = requestAnimationFrame(() => {
                this.handleScroll();
                this._rafId = null;
            });
        };

        // Passive listener for performance
        this._scrollTarget.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    handleScroll() {
        if (!this.element) return;

        let currentScrollY = 0;
        let currentScrollX = 0;

        // 1. Get Scroll Position
        if (this._scrollTarget !== window) {
            currentScrollY = this._scrollTarget.scrollTop;
            currentScrollX = this._scrollTarget.scrollLeft;
        } else {
            currentScrollY = window.scrollY || document.documentElement.scrollTop;
            currentScrollX = window.scrollX || document.documentElement.scrollLeft;
        }

        // 2. Safety Guards
        if (currentScrollY < 0 || currentScrollX < 0) return; // iOS bounce protection

        // 3. Always Show at Top/Start (Safe Area)
        // If Y is near top OR X is near start, showing is safer for UX
        if (currentScrollY < this.SAFE_AREA_TOP && currentScrollX < this.SAFE_AREA_TOP) {
            this.show();
            this._lastScrollY = currentScrollY;
            this._lastScrollX = currentScrollX;
            return;
        }

        // 4. Determine delta
        const deltaY = currentScrollY - this._lastScrollY;
        const deltaX = currentScrollX - this._lastScrollX;

        // 5. Determine Dominant Axis (Universal Logic)
        const isHorizontalMove = Math.abs(deltaX) > Math.abs(deltaY);

        // 6. Action based on Dominant Axis
        if (isHorizontalMove) {
            // Horizontal Mode Logic (X-Axis)
            if (Math.abs(deltaX) > this.DELTA_THRESHOLD) {
                if (deltaX > 0) {
                    // Scrolling RIGHT (Next) -> Hide
                    this.hide();
                } else {
                    // Scrolling LEFT (Prev) -> Show
                    this.show();
                }
            }
        } else {
            // Vertical Mode Logic (Y-Axis)
            if (Math.abs(deltaY) > this.DELTA_THRESHOLD) {
                if (deltaY > 0) {
                    // Scrolling DOWN (Next) -> Hide
                    this.hide();
                } else {
                    // Scrolling UP (Prev) -> Show
                    this.show();
                }
            }
        }

        this._lastScrollY = currentScrollY;
        this._lastScrollX = currentScrollX;
    }

    show() {
        if (!this.isVisible && this.element) {
            this.element.classList.remove('-translate-y-full');
            this.element.classList.add('translate-y-0');
            this.isVisible = true;
        }
    }

    hide() {
        if (this.isVisible && this.element) {
            this.element.classList.remove('translate-y-0');
            this.element.classList.add('-translate-y-full');
            this.isVisible = false;
        }
    }

    toggle() {
        if (this.isVisible) this.hide();
        else this.show();
    }

    destroy() {
        if (this._scrollHandler && this._scrollTarget) {
            this._scrollTarget.removeEventListener('scroll', this._scrollHandler);
        }
        if (this._rafId) cancelAnimationFrame(this._rafId);

        if (this.element) this.element.remove();
        if (window.activeViewerHeader === this) window.activeViewerHeader = null;
    }
}
